import apiService from './apiService';
import { API_BASE_URL } from '../config/api';
import { authFetch } from './session';

export interface BlockedNumberEntry {
  id: string;
  number: string;
  normalized_number: string;
  created_at?: string | null;
}

export interface PhoneRulesResponse {
  allowed_country_prefixes: string[];
  blocked_country_prefixes: string[];
  blocked_numbers: BlockedNumberEntry[];
  monthly_budget: number | null;
  current_month_cost: number;
  budget_exceeded: boolean;
  budget_exceeded_whatsapp_text: string | null;
  budget_exceeded_voice_text: string | null;
  has_voice_audio: boolean;
}

export interface PhoneRulesUpdate {
  allowed_country_prefixes: string[];
  blocked_country_prefixes: string[];
  monthly_budget: number | null;
  budget_exceeded_whatsapp_text: string | null;
  budget_exceeded_voice_text: string | null;
}

class PhoneRulesService {
  async getRules(phoneNumber?: string | null): Promise<PhoneRulesResponse> {
    // Limits of one agent (falling back to the company row) or, without a
    // number, the company row itself.
    const q = phoneNumber ? `?phone_number=${encodeURIComponent(phoneNumber)}` : '';
    return apiService.get<PhoneRulesResponse>(`/phone-rules${q}`);
  }

  async updateRules(
    payload: PhoneRulesUpdate,
    phoneNumber?: string | null,
  ): Promise<PhoneRulesResponse> {
    const body = phoneNumber ? { ...payload, phone_number: phoneNumber } : payload;
    return apiService.put<PhoneRulesResponse>('/phone-rules', body);
  }

  async addBlockedNumber(
    number: string,
    note?: string,
    phoneNumber?: string | null,
  ): Promise<BlockedNumberEntry> {
    // Without an agent number the caller is blocked on every agent.
    return apiService.post<BlockedNumberEntry>('/phone-rules/blocklist', {
      number,
      note,
      phone_number: phoneNumber || undefined,
    });
  }

  async removeBlockedNumber(id: string): Promise<{ success: boolean }> {
    return apiService.delete<{ success: boolean }>(`/phone-rules/blocklist/${id}`);
  }

  async generateBudgetAudio(text: string): Promise<Blob> {
    const response = await authFetch(`${API_BASE_URL}/phone-rules/generate-budget-audio`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ text }),
    });
    if (!response.ok) {
      throw new Error(`Audio generation failed: ${response.status}`);
    }
    return response.blob();
  }
}

export const phoneRulesService = new PhoneRulesService();
export default phoneRulesService;
